<template>
  <LeftSidebar
    :visible="visible"
    baseClass=""
    :title="$lang.editPerson"
    :hasHeaderRightIcon="false"
  >
    <template>
      <b-row class="margin-0" v-if="visible">
        <b-col cols="12" class="padding-16">
          <Input
            v-if="isPlanningPerson === 0"
            :title="$lang.personCenterExecutiveCode"
            :label="$lang.personCenterExecutiveCode"
            :placeholder="$lang.personCenterExecutiveCode"
            :value="form.personCenterExecuteCode"
            name="personCenterExecuteCode"
            id="personCenterExecuteCode"
            type="text"
            :vuelidateObj="$v.form.personCenterExecuteCode"
            @input="input"
            @focus="resetValidation"
            @blur="checkValidation"
          />
          <Dropdown
            :title="oneDropdownTitle"
            :label="oneDropdownTitle"
            :placeholder="oneDropdownTitle"
            :emptyLabel="oneDropdownEmptyLabel"
            :infoTooltip="
              isCandidateSearchWithNationalCode ||
              isExecutivePersonSearchWithNationalCode
                ? $lang.searchWithNationalCode
                : $lang.searchCandidateInfo
            "
            :value="form.oneDropdown"
            name="oneDropdown"
            id="oneDropdown"
            :vuelidateObj="$v.form.oneDropdown"
            :asyncSearch="getOneDropdownCombo"
            :insideFeature="true"
            :shouldShowSearchField="true"
            @input="input"
            @focus="resetValidation"
            @blur="checkValidation"
          />
          <Dropdown
            v-if="personIsAssessor || personIsCenterExecuteManager"
            :title="`${twoDropdownTitle} (${$lang.optional})`"
            :label="twoDropdownTitle"
            :placeholder="twoDropdownTitle"
            :emptyLabel="twoDropdownEmptyLabel"
            :infoTooltip="
              isExecutivePersonSearchWithNationalCode
                ? $lang.searchWithNationalCode
                : $lang.searchCandidateInfo
            "
            :value="form.twoDropdown"
            name="twoDropdown"
            id="twoDropdown"
            :vuelidateObj="$v.form.twoDropdown"
            :asyncSearch="getTwoDropdownCombo"
            :insideFeature="true"
            :shouldShowSearchField="true"
            @input="input"
            @focus="resetValidation"
            @blur="checkValidation"
          />
        </b-col>
      </b-row>
    </template>
    <template #footer>
      <Button
        variant="filled-complementary"
        :text="$lang.cancel"
        class="mobile-width-percent-100 tablet-width-percent-100"
        contentClass="justify-content-center"
        @click="handleClose"
      />
      <Button
        v-if="checkPermission(PERMISSIONS, ['admin', 'edit'])"
        variant="primary"
        :text="$lang.submit"
        class="mobile-width-percent-100 tablet-width-percent-100"
        contentClass="justify-content-center"
        :loading="loading"
        @click="handleSubmit"
      />
    </template>
  </LeftSidebar>
</template>

<script>
import inputMixin from "@/mixin/inputMixin.vue";
import { LeftSidebar } from "@/components/re-design";
import { Input, Dropdown, Button } from "@/components/re-design/forms";
import { required, requiredIf } from "vuelidate/lib/validators";
import { pathOr } from "ramda";
import {
  generateError,
  generateLang,
  renderEumOptions,
  toastAlert,
  checkPermission,
  generateQueryStringWithKeysRepeat,
} from "@/shared/utils";
import {
  getExecutivesCombo,
  getExecutivesWithNationalCode,
  getCandidateCombo,
  getParvandehWithNationalCodeCombo,
} from "@/services";
import { updateCenterExecutePerson } from "@/modules/kanoon/services";

export default {
  mixins: [inputMixin],
  inject: ["CENTER_EXECUTE_DATA", "PERMISSIONS", "handleReloadList"],
  components: { LeftSidebar, Input, Dropdown, Button },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      default: null,
    },
  },
  validations() {
    return {
      form: {
        personCenterExecuteCode: {
          required: requiredIf(() => this.isPlanningPerson === 0),
        },
        oneDropdown: { required },
        twoDropdown: {},
      },
    };
  },
  data() {
    return {
      form: {
        personCenterExecuteCode: null,
        oneDropdown: null,
        twoDropdown: null,
      },
      loading: false,
    };
  },
  computed: {
    executiveRoleOptions() {
      const options = renderEumOptions("executive_role", true);
      return options.filter(({ id }) => id > 2);
    },
    isPlanningPerson() {
      return pathOr(0, ["isPlanningPerson"], this.item);
    },
    executiveRoleId() {
      return pathOr(null, ["executiveRoleId"], this.item);
    },
    personIsCandidate() {
      return this.executiveRoleId === 1;
    },
    personIsAssessor() {
      return this.executiveRoleId === 2;
    },
    personIsCenterExecuteManager() {
      return this.executiveRoleId === 3;
    },
    oneDropdownTitle() {
      const infoTooltip =
        this.isCandidateSearchWithNationalCode ||
        this.isExecutivePersonSearchWithNationalCode
          ? ` (${this.$lang.searchWithNationalCode})`
          : "";
      const caption = pathOr("", ["executiveRoleCaption"], this.item);
      return `${caption}${infoTooltip}`;
    },
    twoDropdownTitle() {
      const infoTooltip = this.isExecutivePersonSearchWithNationalCode
        ? ` (${this.$lang.searchWithNationalCode})`
        : "";
      const caption = this.personIsAssessor
        ? generateLang(["secondAssessor"])
        : generateLang(["kanoonAssistantManager"]);
      return `${caption}${infoTooltip}`;
    },
    isCandidateSearchWithNationalCode() {
      return pathOr(false, ["candidateSearchWithNationalCode"], this.item);
    },
    isExecutivePersonSearchWithNationalCode() {
      return pathOr(
        false,
        ["executivePersonSearchWithNationalCode"],
        this.item
      );
    },
    oneDropdownEmptyLabel() {
      return this.isCandidateSearchWithNationalCode ||
        this.isExecutivePersonSearchWithNationalCode
        ? this.$lang.nationalCode
        : "";
    },
    twoDropdownEmptyLabel() {
      return this.isExecutivePersonSearchWithNationalCode
        ? this.$lang.nationalCode
        : "";
    },
  },
  watch: {
    item: {
      immediate: true,
      handler(value) {
        this.resetForm();
        if (value) {
          const { personCenterExecuteCode, oneDropdown, twoDropdown } = value;
          this.input("personCenterExecuteCode", personCenterExecuteCode);
          this.input("oneDropdown", oneDropdown);
          this.input("twoDropdown", twoDropdown);
        }
      },
    },
  },
  methods: {
    checkPermission,
    handleClose() {
      this.$emit("handleClose");
    },
    checkSearchingWithNationalCode() {
      return this.personIsCandidate
        ? this.isCandidateSearchWithNationalCode
        : this.isExecutivePersonSearchWithNationalCode;
    },
    async getOneDropdownCombo(text = null) {
      try {
        const searchWithNationalCode = this.checkSearchingWithNationalCode();

        // get term code of center execute
        const termCode = pathOr(null, ["term", "id"], this.CENTER_EXECUTE_DATA);

        // get term code of executive person term filter
        let executivePersonTermCodes = pathOr(
          [],
          ["executivePersonTermFilter"],
          this.item
        );
        executivePersonTermCodes = executivePersonTermCodes.map(
          ({ code }) => code
        );

        let params = generateQueryStringWithKeysRepeat(
          executivePersonTermCodes,
          "term_code"
        );

        if (text) {
          params = params.replace("?", "&");
        }

        const response = this.personIsCandidate
          ? // get combo for candidate
            searchWithNationalCode
            ? await getParvandehWithNationalCodeCombo(text, {
                term_code: termCode,
              }) // with national code
            : await getCandidateCombo(text, { term_code: termCode }) // without national code
          : // get combo for executive
          searchWithNationalCode
          ? await getExecutivesWithNationalCode(text, params) // with national code
          : await getExecutivesCombo(text, params); // without national code

        const { options } = response;
        return options;
      } catch (error) {
        // generateError(error);
      }
    },
    async getTwoDropdownCombo(text = null) {
      const searchWithNationalCode = this.checkSearchingWithNationalCode();

      // convert term codes to query staring
      let executivePersonTermFilter = pathOr(
        [],
        ["executivePersonTermFilter"],
        this.item
      );
      executivePersonTermFilter = executivePersonTermFilter.map(
        ({ code }) => code
      );
      let params = generateQueryStringWithKeysRepeat(
        executivePersonTermFilter,
        "term_code"
      );

      if (text) {
        params = params.replace("?", "&");
      }

      try {
        const response = searchWithNationalCode
          ? await getExecutivesWithNationalCode(text, params) // with national code
          : await getExecutivesCombo(text, params); // without national code
        const { options } = response;
        return options;
      } catch (error) {
        // generateError(error);
      }
    },
    async handleSubmit() {
      this.$v.$touch();
      if (this.$v.$anyError) return;

      try {
        this.loading = true;
        const { id: centerExecuteId } = this.CENTER_EXECUTE_DATA;
        const personCenterExecuteId = pathOr(null, ["id"], this.item);
        const { personCenterExecuteCode } = this.form;
        const parvandehCode = pathOr(null, ["oneDropdown", "id"], this.form);
        const secondParvandehCode = pathOr(
          null,
          ["twoDropdown", "id"],
          this.form
        );
        const executiveRoleId = pathOr(
          null,
          ["executiveRole", "id"],
          this.form
        );

        const response = await updateCenterExecutePerson({
          centerExecuteId,
          personCenterExecuteId,
          personCenterExecuteCode,
          parvandehCode,
          secondParvandehCode,
          executiveRoleId,
        });
        const { message } = response;
        toastAlert(message);
        this.handleClose();
        this.handleReloadList();
      } catch (error) {
        generateError(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
